/**
 * Single source of truth for every tunable shared by client & server.
 *
 * Nothing in `shared/` may read wall-clock time or use `Math.random`; all
 * simulation is driven by these constants plus explicit inputs.
 */

/**
 * Bumped whenever the wire format changes incompatibly. One line per version:
 *  2  `welcome.mapSource` — maps generated on demand, downloaded over HTTP.
 *  3  combat: fire/respawn msgs, shot/hit/death events, health + weapon in YouSnap.
 *  4  items: invOp/open/close/pickup/useMed, inv/containerState/invResult/
 *     worldItems, itemDropped/itemTaken, gear on EntitySnap, medEndTick.
 *  5  scavs + modding: attach/detach ops, `mods` on ItemInstance, `weaponId` on shot.
 *  6  meta: queue/buy/sell msgs, profile/queueStatus/raidEnd, raidEndTick, extract.
 *  7  hideout verbs and `hideout` on ProfileWire.
 *  8  `signs` on MapDef (changes every map hash).
 *  9  nested containers: `contents` on ItemInstance, `"inside"` TargetRef, storage boxes.
 * 10  friends and party msgs, friends/partyState/partyInvited.
 * 11  detachable magazines: `magCapacity`/`caliber` on parts, `load` op,
 *     `mag`/`ammoDefId` on a loose magazine (a fitted one still hoisted onto the weapon).
 * 12  rounds live on the magazine, fitted or loose; weapon carries neither key.
 *     `unload` op; R is a magazine swap on every gun but the shotgun.
 * 13  quest items: `questTurnIn` msg, `addresses` on MapDef (hash change);
 *     `bringItems` no longer advances at the settlement.
 * 14  `BTN.NOCLIP` (a v13 server rejects the unknown bit).
 * 15  armour repair: `repair` msg, `maxDurability` on an instance.
 * 16  `reload` on EntitySnap (what the two-handed hold animates against).
 * 17  leaning: `BTN.LEAN_L/R` up, `lean` on EntitySnap down (moves the hitboxes).
 * 18  parts host parts: nested `mods` (capped at `MAX_MOD_DEPTH`), `hostIid` on attach/detach.
 * 19  every gun modular: integral parts became factory parts, `defaultZoom`
 *     deleted, receivers carved, vital `barrel` slot. Content only; schema v8 migrates stashes.
 * 20  `BTN.TACTICAL` up, `tac` on EntitySnap down.
 * 21  a corpse's `containerState` carries `equipment`; its `grid` is the pockets.
 * 22  rigs have pouches: optional `zones` on `GridState` — placement must agree.
 * 23  English def ids for every weapon, part and magazine (+ `craft_foregrip`); schema v10 rewrites stashes.
 * 24  `team` on EntitySnap, `env` on welcome, grenade kind and msgs/events.
 * 25  progression: REQUIRED `progress` on ProfileWire, xp/level on RaidSummary,
 *     `skillMods` on welcome (read by prediction), `role` on EntitySnap, reason "level".
 * 26  hunger/thirst: `needs` on YouSnap (read by prediction), `consume` on groceries via `useMed`.
 * 27  counted magazines: `magKnown`, `magCheck` msg, `unknown`/`checking` on YouSnap.weapon.
 * 28  wildlife: `EntityKind` "animal" + `species`, `birdFlush` event.
 * 29  snow (fourth `Weather`) and `tracks` on Snapshot.
 * 30  trader loyalty: REQUIRED `loyalty` on ProfileWire, `minTier`/`afterQuest`
 *     gates, reasons "tier"/"quest".
 * 31  walkable shelter: `hideoutTrain` msg, REQUIRED `training` on HideoutState,
 *     three new modules (the Funkraum multiplies xp).
 * 32  death forensics: REQUIRED `cause` + optional `killerName`/`killerEye`/
 *     `distM`/`weaponId`/`limb` on the death event.
 * 33  scav barks: `bark` event (kind + variant).
 * 34  breakable glass: `MapDef.glass`, `glass` event/msg, `glassState`.
 */
export const PROTOCOL_VERSION = 34;

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/** Simulation ticks per second. */
export const TICK_RATE = 30;
/** Fixed simulation timestep, in seconds. Movement is only ever stepped by this. */
export const TICK_DT = 1 / 30;
/** Snapshots are emitted every N ticks (30 / 2 = 15 Hz). */
export const SNAPSHOT_EVERY_N_TICKS = 2;
/** How far in the past remote entities are rendered, in milliseconds. */
export const INTERP_DELAY_MS = 150;
/**
 * How long a magazine check takes. One number for the server's clock and the
 * client's animation. Short on purpose — a glance, not a reload.
 */
export const MAG_CHECK_SEC = 0.9;

// ---------------------------------------------------------------------------
// Movement
// ---------------------------------------------------------------------------

/** Horizontal target speed while walking, m/s. */
export const WALK_SPEED = 4.2;
/** Horizontal target speed while sprinting, m/s. */
export const SPRINT_SPEED = 6.5;
/** Horizontal target speed while crouched, m/s. */
export const CROUCH_SPEED = 2.0;

/** Ground acceleration toward the wish velocity, m/s^2. */
export const ACCEL_GROUND = 40;
/** Air acceleration (weak air control), m/s^2. */
export const ACCEL_AIR = 6;
/** Ground friction coefficient applied when there is no move input. */
export const FRICTION_GROUND = 10;

/** Instantaneous upward velocity applied on jump, m/s. */
export const JUMP_VELOCITY = 4.5;
/** Downward acceleration, m/s^2. */
export const GRAVITY = 18;

/** Fly speed while noclipping (the F6 debug mode), m/s. */
export const NOCLIP_SPEED = 10;
/** Fly-speed multiplier while noclipping with SPRINT held. */
export const NOCLIP_SPRINT_MULT = 5;

// ---------------------------------------------------------------------------
// Capsule / eye
// ---------------------------------------------------------------------------

export const CAPSULE_RADIUS = 0.35;
export const CAPSULE_HEIGHT_STAND = 1.8;
export const CAPSULE_HEIGHT_CROUCH = 1.3;

export const EYE_HEIGHT_STAND = 1.62;
export const EYE_HEIGHT_CROUCH = 1.2;

/** Maximum ledge height the capsule can step onto without jumping, metres. */
export const STEP_UP_MAX = 0.45;

// ---------------------------------------------------------------------------
// Encumbrance. One RAMP, not a cliff: no penalty below `CARRY_LIGHT_KG`, no
// further one above `CARRY_MAX_KG`, linear between — so every kilogram costs
// a little and "is this last plate worth it" is a decision. Speed drops to
// 0.6; ACCELERATION to 0.35, and since `stepMovement` scales friction by the
// same number the heavy player is equally slow to stop — that is the inertia.
// `CARRY_LIGHT_KG` sits under the default loadout so the mechanic is visible
// from the first raid.
// ---------------------------------------------------------------------------

/** At or below this many kilograms, nothing is slowed at all. */
export const CARRY_LIGHT_KG = 12;
/** At or above this, the penalty is at maximum and stops growing. */
export const CARRY_MAX_KG = 40;
/** Top-speed multiplier at `CARRY_MAX_KG` and beyond. */
export const CARRY_SPEED_MIN_MULT = 0.6;
/** Acceleration AND friction multiplier at `CARRY_MAX_KG` and beyond. */
export const CARRY_ACCEL_MIN_MULT = 0.35;

// ---------------------------------------------------------------------------
// Stamina
// ---------------------------------------------------------------------------

export const STAMINA_MAX = 100;
/** Stamina consumed per second while sprinting. */
export const STAMINA_SPRINT_DRAIN = 12;
/** Stamina recovered per second while not sprinting. */
export const STAMINA_REGEN = 8;

// ---------------------------------------------------------------------------
// Looting
// ---------------------------------------------------------------------------

/**
 * How close you must be to touch a container, corpse or dropped item — feet to
 * the nearest point of the interact volume. Generous enough that the client
 * never has to guess; walls are thicker than this, so geometry does the occlusion.
 */
export const LOOT_REACH_M = 2;

/**
 * Feet-to-pane-box distance within which a player can smash or climb a window
 * (`LOOT_REACH_M` is 2; the sill is ~1 m up).
 */
export const GLASS_REACH_M = 2.2;

/**
 * Minimum spacing between inventory ops from one connection — each
 * re-serializes a container to every viewer. Server-side only.
 */
export const INV_OP_MIN_INTERVAL_MS = 100;

// ---------------------------------------------------------------------------
// Social
// ---------------------------------------------------------------------------

/**
 * Most players in one party. Four: a squad that fills a raid is a private
 * lobby, and four fits around one spawn point. Enforced server-side on invite
 * and accept; the client only greys the button.
 */
export const PARTY_MAX = 4;
